import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboardStore } from '@/lib/stores/dashboardStore';
import { useAuthStore } from '@/lib/stores/authStore';
import { useTheme } from '@/hooks/useTheme';
import { Button } from '@/components/ui/button';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import CharacterCard from '@/components/dashboard/CharacterCard';
import PartyCard from '@/components/dashboard/PartyCard';
import SessionCard from '@/components/dashboard/SessionCard';
import { toast } from '@/lib/toast';
import { apiClient } from '@/lib/api';
import { registerPasskey } from '@/lib/passkey-service';
import { convertToAccount, convertWithGoogle } from '@/lib/auth-service';
import { Input } from '@/components/ui/input';
import { DISCORD_JOIN_GRANT_PM, MIN_PM_BALANCE, REFERRAL_GRANT_PM } from '@danda/shared';
import { shouldGateToApp } from '@/lib/platform';
import { nextPmBalanceFetchSeq, usePmBalanceStore } from '@/lib/stores/pmBalanceStore';
import { useAccountModalStore } from '@/lib/stores/accountModalStore';
import { track } from '@/lib/analytics';

export default function DashboardPage() {
  const navigate = useNavigate();
  const openAccount = useAccountModalStore((s) => s.openModal);
  const { character, party, resumeContext, loading, error, hasFetched, fetch } =
    useDashboardStore();
  const user = useAuthStore((s) => s.user);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [pmBalance, setPmBalance] = useState<number | null>(null);
  const [showPasskeyOffer, setShowPasskeyOffer] = useState(
    () =>
      sessionStorage.getItem('offer-passkey-setup') === 'true' &&
      !useAuthStore.getState().user?.passkeyRegistered
  );
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  // Guest → full-account conversion (links email+password to the same anon uid).
  const isGuest = user?.isAnonymous ?? false;
  const [convertEmail, setConvertEmail] = useState('');
  const [convertPassword, setConvertPassword] = useState('');
  const [convertError, setConvertError] = useState<string | null>(null);
  const [convertLoading, setConvertLoading] = useState(false);
  // True only on the first dashboard render right after signup (SignupPage sets
  // this one-shot flag). Read here but clear in an effect, NOT in the initializer:
  // React StrictMode double-mounts in dev, and clearing during the throwaway
  // first mount would leave the visible second mount reading a returning user.
  // Both mounts' initializers read the still-present flag → isNewSignup stays
  // true; the effect clears it so a reload reverts to the returning-user copy.
  const [isNewSignup] = useState(() => sessionStorage.getItem('new-signup') === '1');
  useEffect(() => {
    if (isNewSignup) sessionStorage.removeItem('new-signup');
  }, [isNewSignup]);
  // Lobby entry is free under the new policy — no CTA is disabled by
  // balance. The inline banner is the only surface that branches on it.
  // Direct `pmBalance !== null` narrows TS cleanly; an intermediate
  // `balanceLoading` alias would leave `null` in the union on the right
  // side of `&&` even though runtime behavior is the same (`null < 1`
  // is `false`).
  const showLowBalanceBanner = pmBalance !== null && pmBalance < MIN_PM_BALANCE;
  const userName = user?.displayName || user?.email?.split('@')[0] || 'adventurer';

  useEffect(() => {
    fetch();
    toast.dismiss('session-ending');
    const seq = nextPmBalanceFetchSeq();
    apiClient
      .getPmBalance()
      .then(({ pmTokenBalance }) => {
        setPmBalance(pmTokenBalance);
        // Hydrate the in-chat gate's store so a player who tops up via
        // /buy-tokens then immediately enters the lobby doesn't see the
        // gate flash while the lobby WS connects + pushes
        // `lobby:balance-update`.
        usePmBalanceStore.getState().setPmBalance(pmTokenBalance, seq);
      })
      .catch(() => {});
  }, [fetch]);

  // Show message if redirected from session takeover (single-frontend enforcement)
  // Delay removal so StrictMode's double-effect doesn't swallow the toast
  useEffect(() => {
    const moved = sessionStorage.getItem('session-moved');
    if (!moved) return;
    const timeout = setTimeout(() => {
      sessionStorage.removeItem('session-moved');
      toast.info('Session moved', {
        description: 'You opened the app in another window. This tab has been disconnected.',
      });
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  const dismissPasskeyOffer = useCallback(() => {
    sessionStorage.removeItem('offer-passkey-setup');
    setShowPasskeyOffer(false);
  }, []);

  // Redirect to the install-store gate only when the user is actually on
  // a phone/tablet AND the viewport is narrow. A desktop user resizing
  // their window narrow stays on the lobby flow rather than being kicked
  // into the "install the app" funnel.
  const handleEnterLobby = useCallback(() => {
    if (shouldGateToApp()) {
      navigate('/get-the-app');
    } else {
      navigate('/lobby');
    }
  }, [navigate]);

  const handleResumeSession = useCallback(
    (sessionId: string) => {
      if (shouldGateToApp()) {
        navigate('/get-the-app');
      } else {
        navigate(`/play/${sessionId}`);
      }
    },
    [navigate]
  );

  const handleSetupPasskey = useCallback(async () => {
    setPasskeyLoading(true);
    try {
      const success = await registerPasskey();
      if (success) {
        useAuthStore.getState().updateProfile({ passkeyRegistered: true });
        toast.success('Passkey registered! Use it to sign in next time.');
      }
    } catch (err) {
      console.error('Passkey setup failed:', err);
      toast.error('Passkey setup failed. You can try again later in settings.');
    } finally {
      setPasskeyLoading(false);
      dismissPasskeyOffer();
    }
  }, [dismissPasskeyOffer]);

  const handleConvert = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setConvertError(null);
      if (convertPassword.length < 6) {
        setConvertError('Password must be at least 6 characters.');
        return;
      }
      setConvertLoading(true);
      try {
        await convertToAccount(convertEmail, convertPassword);
        toast.success('Account saved! You can now buy tokens and sign in on any device.');
        setConvertEmail('');
        setConvertPassword('');
      } catch (err) {
        setConvertError(
          err instanceof Error ? err.message : 'Could not save your account. Please try again.'
        );
      } finally {
        setConvertLoading(false);
      }
    },
    [convertEmail, convertPassword]
  );

  const handleConvertGoogle = useCallback(async () => {
    setConvertError(null);
    setConvertLoading(true);
    try {
      // Popup resolves inline; on success setUser flips isAnonymous and this card unmounts.
      await convertWithGoogle();
      toast.success('Account saved with Google! You can now buy tokens and sign in anywhere.');
    } catch (err) {
      setConvertError(
        err instanceof Error ? err.message : 'Could not sign in with Google. Please try again.'
      );
    } finally {
      setConvertLoading(false);
    }
  }, []);

  return (
    <div className="relative min-h-screen">
      {loading && (
        <div className="relative z-[3] flex items-center justify-center min-h-screen">
          <AnkhSpinner size="lg" className="text-accent-gold" />
        </div>
      )}

      {!loading && error && (
        <div className="relative z-[3] flex flex-col items-center justify-center min-h-screen space-y-4 px-6">
          <p className="text-destructive">{error}</p>
          <Button variant="outline" onClick={() => fetch()}>
            Retry
          </Button>
        </div>
      )}

      {!loading && !error && (
        <div className="relative z-[3] mx-auto max-w-2xl px-6 py-12 pt-24">
          <div className="space-y-6">
            {/* Logo + tagline */}
            <div className="text-center mb-8">
              <img
                src={
                  isDark ? '/polymyth_logo_v1_final_dark.png' : '/polymyth_logo_v1_final_lite.png'
                }
                alt="Polymyth"
                className="h-16 w-auto mx-auto mb-5 drop-shadow-lg"
              />
              <p className="text-3xl text-foreground/90 tracking-wide font-character">
                {isNewSignup ? `Welcome, ${userName}` : `Welcome Back ${userName}`}
              </p>
              <p className="text-3xl text-foreground/90 tracking-wide font-character">
                {isNewSignup ? 'Your adventure begins!' : 'Let the Adventure Begin!'}
              </p>
            </div>

            {/* Guest → save account. Links email+password to the same anonymous
              uid, so every character / session / token carries over. Unlocks
              buying tokens + password-reset recovery + cross-device sign-in. */}
            {isGuest && (
              <div className="rounded-xl border border-[hsl(var(--accent-gold))]/40 bg-card/60 text-card-foreground shadow-lg backdrop-blur-md p-6">
                <h3 className="text-lg font-bold text-foreground mb-2 text-center">
                  Save Your Account
                </h3>
                <p className="text-sm text-muted-foreground mb-4 text-center">
                  You're playing as a guest. Add an email and password to keep your hero, play on
                  any device, and buy more tokens when your gift runs low.
                </p>
                <Button
                  type="button"
                  onClick={handleConvertGoogle}
                  disabled={convertLoading}
                  variant="outline"
                  className="w-full mb-3"
                >
                  Continue with Google
                </Button>
                <div className="relative mb-3">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">or</span>
                  </div>
                </div>
                <form onSubmit={handleConvert} className="space-y-3">
                  {convertError && (
                    <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive border border-destructive/30">
                      {convertError}
                    </div>
                  )}
                  <Input
                    type="email"
                    name="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode="email"
                    placeholder="your@email.com"
                    value={convertEmail}
                    onChange={(e) => setConvertEmail(e.target.value)}
                    required
                  />
                  <Input
                    type="password"
                    name="new-password"
                    autoComplete="new-password"
                    placeholder="Password (6+ characters)"
                    value={convertPassword}
                    onChange={(e) => setConvertPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                  <Button
                    type="submit"
                    className="w-full bg-[hsl(var(--accent-gold))] text-white hover:bg-[hsl(var(--accent-gold))]/90"
                    disabled={convertLoading}
                  >
                    {convertLoading ? 'Saving...' : 'Save Account'}
                  </Button>
                </form>
              </div>
            )}

            {/* Passkey setup offer — shown once after email/password sign-in */}
            {showPasskeyOffer && (
              <div className="rounded-xl border border-primary/30 bg-card/60 text-card-foreground shadow-lg backdrop-blur-md p-6 text-center">
                <h3 className="text-lg font-bold text-foreground mb-2">Set Up Passkey</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Use Touch ID or Face ID for faster sign-ins next time.
                </p>
                <div className="flex gap-3 justify-center">
                  <Button
                    className="bg-[hsl(var(--accent-gold))] text-white hover:bg-[hsl(var(--accent-gold))]/90"
                    onClick={handleSetupPasskey}
                    disabled={passkeyLoading}
                  >
                    {passkeyLoading ? 'Setting up...' : 'Set Up Passkey'}
                  </Button>
                  <Button variant="ghost" onClick={dismissPasskeyOffer}>
                    Not Now
                  </Button>
                </div>
              </div>
            )}

            {/* Cliffhanger resume card, live session or ended-incomplete */}
            <SessionCard
              resumeContext={resumeContext}
              isLeader={!!character && party?.leaderCharacterId === character.id}
              finalGameTimeSeconds={party?.lastSessionEnd?.finalGameTimeSeconds}
              disabled={false}
              onResume={handleResumeSession}
              onEnterLobby={handleEnterLobby}
            />

            {/* Character card */}
            {character ? <CharacterCard character={character} onClick={handleEnterLobby} /> : null}

            {/* Party card */}
            {party && <PartyCard party={party} />}

            {/* Low-balance banner — informational only; lobby entry is free.
              Tapping Get Tokens opens the token store. The actual spend
              gate lives in the in-chat surface (ChatPanel.handleSendMessage). */}
            {showLowBalanceBanner && (
              <div className="rounded-xl border border-amber-500/50 bg-amber-950/40 text-card-foreground shadow-lg backdrop-blur-md p-6 text-center">
                <div className="text-4xl mb-3">&#x26A0;</div>
                <h3 className="text-lg font-bold text-amber-200 mb-2">Out of Polymyth Tokens</h3>
                <p className="text-sm text-amber-200/80 mb-2">
                  Your balance is {pmBalance?.toFixed(2)} PMT. Here are three ways to get more:
                </p>
                <ul className="text-sm text-amber-200/80 mb-4 space-y-1 text-left inline-block">
                  <li>
                    <strong>Bring a friend</strong> and you each get {REFERRAL_GRANT_PM} tokens,
                    enough to play Goldvale through. Party costs split between you, so it stretches
                    further too.
                  </li>
                  <li>
                    <strong>Join our Discord</strong> for {DISCORD_JOIN_GRANT_PM} tokens.
                  </li>
                  <li>
                    <strong>Buy tokens</strong> whenever you'd rather not wait.
                  </li>
                </ul>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button
                    className="bg-amber-500 text-amber-950 hover:bg-amber-400"
                    onClick={() => {
                      track('invite_cta_click', { surface: 'dashboard_banner', low_balance: true });
                      openAccount('invite');
                    }}
                  >
                    Invite friends
                  </Button>
                  <Button
                    variant="outline"
                    className="border-amber-500/50 text-amber-200 hover:bg-amber-500/20"
                    onClick={() => navigate('/buy-tokens')}
                  >
                    Buy Tokens
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    track('earn_more_cta_click', {
                      surface: 'dashboard_banner',
                      low_balance: true,
                    });
                    openAccount('profile');
                  }}
                  className="mt-3 text-xs text-amber-200/70 underline underline-offset-2 hover:text-amber-200"
                >
                  Additional ways to earn tokens
                </button>
              </div>
            )}

            {/* Empty state — only shown after `fetch()` has resolved at least
              once with no character. Gating on `hasFetched` (a store sentinel
              set inside `fetch()` after success OR error) instead of
              `!loading` prevents the empty card from briefly flashing on a
              dashboard remount: the store may still carry `loading: false`
              from a prior fetch, and the post-mount `useEffect`'s `fetch()`
              hasn't run yet to flip it back to true. */}
            {hasFetched && !loading && !error && !character && (
              <div className="rounded-xl border bg-card/40 text-card-foreground shadow-lg backdrop-blur-md p-6 text-center">
                <div className="text-4xl mb-3">☥</div>
                <h3 className="text-lg font-bold text-foreground mb-2">No Character Yet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Head to the lobby to create your first character and to start your adventure.
                </p>
              </div>
            )}

            {/* Primary CTA — always enabled. Lobby entry is free; the
              spend-time gate lives in the in-chat surface. */}
            <Button className="w-full py-6 text-base" onClick={handleEnterLobby}>
              Enter Polymyth
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
